<template>
  <PoPanel
    v-if="detail"
    :detail="detail"
    @mark-shipped="emit('mark-shipped', $event)"
    @shipping-details="emit('shipping-details', $event)"
    @request-delivery="emit('request-delivery', $event)"
  />
  <template v-else>
    <p class="sr-only" role="status">Loading {{ poNumber }}</p>
    <v-progress-linear indeterminate color="primary" height="2" :aria-label="`Loading ${poNumber}`" />
  </template>
</template>

<script setup lang="ts">
  import { useQuery } from '@pinia/colada';
  import { computed, nextTick, watch } from 'vue';

  import PoPanel from '@/components/poTracker/PoPanel.vue';
  import logger from '@/services/logger';
  import { ALWAYS_STALE, poDetailKey, poDetailQuery, usePoTrackerStore } from '@/store/poTrackerStore';
  import type { PoDetail, Shipment, ShippingEvent } from '@/types/poTracker';
  import { errorStatus } from '@/utils/apiError';
  import { DETAIL_FAILURE, detailFailure } from '@/utils/poTracker';

  const props = defineProps<{ poNumber: string }>();
  const emit = defineEmits<{
    'mark-shipped': [shipment: Shipment];
    'shipping-details': [event: ShippingEvent];
    'request-delivery': [poNumber: string];
  }>();

  const store = usePoTrackerStore();
  const vendorId = computed(() => store.url.vendorId ?? '');

  // Mounted only while the PO is expanded: a cached detail shows at once and is refreshed behind it,
  // and collapsing the PO leaves the cache for the next expand. Refetches on focus like the list.
  const query = useQuery<PoDetail>({
    key: () => poDetailKey(vendorId.value, props.poNumber),
    query: () => poDetailQuery(vendorId.value, props.poNumber),
    enabled: () => vendorId.value !== '',
    staleTime: ALWAYS_STALE,
  });
  const detail = computed<PoDetail | null>(() => query.data.value ?? null);

  watch(query.error, (error) => {
    if (!error) return;
    const failure = detailFailure(errorStatus(error), detail.value !== null);
    if (failure === DETAIL_FAILURE.QUIET) return;
    if (failure === DETAIL_FAILURE.GONE) logger.showWarning(`PO ${props.poNumber} is no longer open.`);
    if (failure === DETAIL_FAILURE.RETRY) logger.showWarning(`Couldn't load ${props.poNumber}. Expand it again to retry.`);
    // Collapsed so the next click retries; a gone PO's cache is dropped once this panel has unmounted.
    const { poNumber } = props;
    store.collapse(poNumber);
    if (failure === DETAIL_FAILURE.GONE) void nextTick(() => store.dropDetail(poNumber));
  });
</script>
